import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import { FiArrowLeft, FiMessageSquare, FiPhone, FiMapPin, FiBriefcase, FiUser, FiGlobe, FiCheckCircle, FiEdit, FiMail } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { getSmsManagementById } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { getSmsProcessBadges, hasCustomSmsMessages, parseSmsManagementResponse, SMS_MESSAGE_SECTION_LABELS, SMS_MESSAGE_PROCESS_KEYS, getAllLanguageCodesFromMessages, getSmsLanguageLabel } from '../../constants/smsManagement';
import './AdminSmsDetail.css';
import '../../components/Admin/SmsManagementForm.css';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminSmsDetail = () => {
    const navigate = useNavigate();
    const { smsManagementId } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [smsManagement, setSmsManagement] = useState(null);

    useEffect(() => {
        const fetchSmsManagement = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getSmsManagementById(smsManagementId);
                const { smsManagement: smsMgmt, user } = parseSmsManagementResponse(data);
                setSmsManagement(smsMgmt ? { sms_management: smsMgmt, user } : null);
            } catch (e) {
                setError(handleApiError(e, 'Erreur lors du chargement du SMS management'));
            } finally {
                setLoading(false);
            }
        };
        if (smsManagementId) fetchSmsManagement();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [smsManagementId]);

    if (loading) {
        return (
            <div className="dashboard-page admin-sms-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du SMS Management</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement du SMS management...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page admin-sms-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du SMS Management</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">{error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!smsManagement) {
        return (
            <div className="dashboard-page admin-sms-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du SMS Management</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée SMS management disponible pour l'identifiant: {smsManagementId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    const user = smsManagement.user || {};
    const smsMgmt = smsManagement.sms_management || smsManagement;

    return (
        <div className="dashboard-page admin-sms-detail-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiMessageSquare className="me-2" />
                    Détail du SMS Management
                </h2>
                <div className="d-flex gap-2">
                    <Button variant="primary" onClick={() => navigate(`/admin/sms/edit/${smsManagementId}`)}>
                        <FiEdit className="me-2" />
                        Modifier
                    </Button>
                    <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        <FiArrowLeft className="me-2" /> Retour
                    </Button>
                </div>
            </div>

            <Section title="Informations principales">
                <Row>
                    <Col md={6} className="mb-3">
                        <Label>SMS Management ID</Label>
                        <Value>{smsMgmt.sms_management_id || 'N/A'}</Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>Processus SMS</Label>
                        <Value>
                            {getSmsProcessBadges(smsMgmt.sms_processes).map((label) => (
                                <Badge key={label} bg="info" className="me-1 mb-1">
                                    {label}
                                </Badge>
                            ))}
                        </Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>Messages personnalisés</Label>
                        <Value>
                            {hasCustomSmsMessages(smsMgmt.sms_messages) ? (
                                <Badge bg="success">Oui</Badge>
                            ) : (
                                <Badge bg="secondary">Non</Badge>
                            )}
                        </Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>Date de création</Label>
                        <Value>{formatDate(smsMgmt.created_at)}</Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>Dernière modification</Label>
                        <Value>{formatDate(smsMgmt.updated_at)}</Value>
                    </Col>
                </Row>
            </Section>

            {!smsMgmt.sms_processes?.includes('no_sms') && hasCustomSmsMessages(smsMgmt.sms_messages) && (
                <Section title="Messages SMS personnalisés">
                    <div className="sms-messages-readonly">
                        {SMS_MESSAGE_PROCESS_KEYS.filter((key) => smsMgmt.sms_processes?.includes(key)).map((processKey) => (
                            <div key={processKey} className="mb-4">
                                <h6 className="fw-semibold mb-3">{SMS_MESSAGE_SECTION_LABELS[processKey]}</h6>
                                {getAllLanguageCodesFromMessages(smsMgmt.sms_messages, processKey).map((code) => {
                                    const text = smsMgmt.sms_messages?.[processKey]?.[code];
                                    if (!text) return null;
                                    return (
                                        <div key={code} className="mb-2">
                                            <div className="sms-message-lang">
                                                {getSmsLanguageLabel(code)} ({code})
                                            </div>
                                            <div className="sms-message-text">{text}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {user && Object.keys(user).length > 0 && (
                <Section title="Informations du Partenaire">
                    <Row>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiUser className="me-1" />
                                Prénom
                            </Label>
                            <Value>{user.first_name || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiUser className="me-1" />
                                Nom
                            </Label>
                            <Value>{user.last_name || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiMail className="me-1" />
                                Email
                            </Label>
                            <Value>{user.email || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiPhone className="me-1" />
                                Téléphone
                            </Label>
                            <Value>{user.phone_number || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiBriefcase className="me-1" />
                                Entreprise
                            </Label>
                            <Value>{user.company_name || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiMapPin className="me-1" />
                                Ville
                            </Label>
                            <Value>{user.city || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiGlobe className="me-1" />
                                Pays
                            </Label>
                            <Value>{user.country || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiCheckCircle className="me-1" />
                                Statut
                            </Label>
                            <Value>
                                {user.is_verified ? (
                                    <Badge bg="success">Vérifié</Badge>
                                ) : (
                                    <Badge bg="warning">Non vérifié</Badge>
                                )}
                            </Value>
                        </Col>
                    </Row>
                </Section>
            )}
        </div>
    );
};

export default AdminSmsDetail;
